import { isValid, isAfter, isBefore } from 'date-fns';

export const MAX_CHARS = 50;

export const isRequired = (message: string) => ({
  required: message,
});

export const isValidEmail = (message: string) => ({
  pattern: { value: /^\S+@\S+\.\S+$/, message },
});

export const isValidOrderNumber = (orderNumberMaxLength: number, message: string) => ({
  pattern: {
    value: new RegExp(
      `^([a-zA-Z0-9]{6}(?:[a-zA-Z0-9]{${orderNumberMaxLength - 6}})?|^\\d{4}-\\d{3}-\\d{3})$`,
    ),
    message,
  },
});

export const isValidDate = (message: string) => ({
  validate: {
    checkIsDateValid: (date: unknown) => isValid(date) || message,
  },
});

export const isAfterDate = (minDate: Date | undefined, message: string) => ({
  validate: {
    checkIsAfter: (value: Date) => {
      if (!minDate) {
        return true;
      }

      if (!isValid(value) || !isValid(minDate)) {
        return true;
      }

      if (isAfter(value, minDate)) {
        return true;
      }

      return message;
    },
  },
});

export const isBeforeDate = (maxDate: Date | undefined, message: string) => ({
  validate: {
    checkIsBefore: (value: Date) => {
      if (!maxDate) {
        return true;
      }

      if (!isValid(value) || !isValid(maxDate)) {
        return true;
      }

      if (isBefore(value, maxDate)) {
        return true;
      }

      return message;
    },
  },
});

export const isNotNull = (message: string) => ({
  validate: {
    checkIsNotNull: (value: unknown) => {
      if (value === null) {
        return message;
      }

      return true;
    },
  },
});

const hasMinLength = (message: string, min: number) => ({
  minLength: { value: min, message: message || `Must be longer than ${min} characters` },
});

const onlyContainsValidCharacters = (validCharacters: string, message: string) => ({
  pattern: {
    value: new RegExp(`^[${validCharacters}]*$`),
    message: message || 'One of the characters is not allowed',
  },
});

const matchesField = (otherFieldValue: string, message: string) => ({
  validate: {
    checkNotMatchesField: (value: string) => {
      if (!value) {
        return undefined;
      }

      if (value === otherFieldValue) {
        return message || 'The field has the same value';
      }

      return undefined;
    },
  },
});

const isEmailInBlockedEmailDomains = (blockedEmailDomains: string[], email: string) => {
  return blockedEmailDomains.some((domain: string) =>
    email.toLowerCase().includes(domain.toLowerCase()),
  );
};

export const isBlockedEmail = (blockedEmailDomains: string[] = [], message: string) => ({
  checkBlockedEmail: (value: string) => {
    if (isEmailInBlockedEmailDomains(blockedEmailDomains, value)) {
      return message || 'Invalid email address';
    }

    return undefined;
  },
});

const notMatchesField = (otherFieldValue: string, message: string) => ({
  validate: {
    checkNotMatchesField: (value: string) => {
      if (!value) {
        return undefined;
      }

      if (otherFieldValue.toLowerCase() !== value.toLowerCase()) {
        return message || 'The field does not have the same value';
      }

      return undefined;
    },
  },
});

// React Hook Form input rules(validations)
export const nameValidations = (
  requiredMessage: string,
  minLengthMessage: string,
  onlyContainsValidCharactersMessage: string,
  allowedCharacters: string,
) => {
  return {
    ...isRequired(requiredMessage),
    ...hasMinLength(minLengthMessage, 2),
    ...onlyContainsValidCharacters(allowedCharacters, onlyContainsValidCharactersMessage),
  };
};

export const lastNameValidations = (
  reqiredMessage: string,
  minLengthMessage: string,
  onlyContainsValidCharactersMessage: string,
  allowedCharacters: string,
  firstNameValue: string,
  matchesFieldMessage: string,
) => {
  return {
    ...nameValidations(
      reqiredMessage,
      minLengthMessage,
      onlyContainsValidCharactersMessage,
      allowedCharacters,
    ),
    ...matchesField(firstNameValue, matchesFieldMessage),
  };
};

type MarketingCarrier = {
  code: string;
  name: string;
};

export const rynairEmailValidation = (
  ryanAirEmailValidationEnabled: boolean,
  marketingCarriers: MarketingCarrier[],
  ryanAirEmailErrorMessage: string,
) => ({
  checkEmailNotAllowedInRyanair: (value: string) => {
    const invalidRyanEmailPattern = /_@./;
    const defaultMessage = "Invalid email; '_' before '@' is not allowed for Ryan Air.";
    const hasRyanAsMarketingCarrier = marketingCarriers.some(
      ({ code }) => code === 'FR' || code === 'RK',
    );

    if (!ryanAirEmailValidationEnabled || !hasRyanAsMarketingCarrier) {
      return undefined;
    }

    return invalidRyanEmailPattern.test(value.trim())
      ? ryanAirEmailErrorMessage || defaultMessage
      : undefined;
  },
});

export const confirmEmailValidations = (
  isRequiredMessage: string,
  emailValue: string,
  notMatchesFieldMessage: string,
) => {
  return {
    ...isRequired(isRequiredMessage),
    ...notMatchesField(emailValue, notMatchesFieldMessage),
  };
};
